/**
 * model/cutTarget.ts — Fix CutTarget and notch (PLAN §2.11).
 *
 * It is part of the `model/` layer (SPEC §6): contains the resolution logic
 * of the chain. This function does NOT duplicate the mapping: delegate to
 * `config/cutTargets.ts` (single source of mapping, SPEC G7/R3).
 *
 * The anchor surface moves downstream when the CutTarget changes
 * (SPEC G7, AC-3): Downstream modules (beams/connectors/fasteners) use
 * `notchDepthFor` to stay consistent; the cutting axis is defined in
 * ONLY ONE dot (`model/beams.ts` → `resolveNotch`).*/

import type { CutTarget } from "../data/types";
import { lateralResistanceToCutTarget } from "../config/cutTargets";

/**
 * Fix the CutTarget from the form (PLAN §2.11).
 *
 * Delegate mapping to `config/cutTargets.ts` (DO NOT duplicate). Input null or out
 * range → "none" (robust configurator with incomplete form).
 *
 * @param lateralResistanceConfig The form's `lateralResistanceConfig` value (0–4 or null).
 * @returns CutTarget fixed; "none" for null/out-of-range.*/
export function resolveCutTarget(lateralResistanceConfig: number | null, orientation: "beam" | "column"): CutTarget {
  return lateralResistanceToCutTarget(lateralResistanceConfig, orientation);
}

/**
 * Spazio (mm) fra joist e header lungo X quando NESSUN notch taglia
 * davvero il legno (01-10-2026, richiesta utente: "quando non si sceglie
 * una configurazione laterale di default ci dovrebbe essere uno spazio per
 * il connettore assemblato cosi' come avviene per la configurazione del
 * lockstop ma senza lockstop").
 *
 * PRIMA: lo spazio esisteva SOLO per `lateralResistanceConfig` 3
 * (InclinedFasteners) e 4 (LockStop) — `null`/default e i casi 0/1
 * disallineati dall'orientazione (`lateralResistanceToCutTarget`: "header-
 * column" richiede orientation "column", "header-beam" richiede "beam",
 * altrimenti "none") risultavano con gap 0, cioe' il connettore si
 * sovrapponeva al legno invece di stare in uno spazio riservato.
 *
 * ORA: basato DIRETTAMENTE su `cutTarget` (gia' risolto dal chiamante) invece
 * che su `lateralResistanceConfig` grezzo — `cutTarget === "none"` e'
 * ESATTAMENTE "nessun notch taglia la trave", che copre sia i casi 3/4 di
 * prima sia `null`/default sia i disallineamenti di orientazione: in TUTTI
 * questi casi serve lo stesso spazio riservato per l'assieme connettore,
 * notch o no.
 *
 * @param cutTarget Target di taglio gia' risolto (`resolveCutTarget`).
 * @param assemblyThickness Spessore dell'assieme connettore (mm).
 * @returns Lo spessore dell'assieme se non c'e' notch, 0 altrimenti (il
 * notch stesso ricava lo spazio scavando nel legno — vedi `notchDepthFor`).*/
export function resolveAssemblyGap(cutTarget: CutTarget, assemblyThickness: number): number {
  return cutTarget === "none" ? assemblyThickness : 0;
}

/**
 * Depth of cut (PLAN §2.11, SPEC G7).
 * 0 if target is "none"; otherwise the thickness of the assembly
 * (`connector_assembly.thickness`, ex. 22).
 *
 * @param cutTarget Cut target fixed.
 * @param assemblyThickness Thickness of the connector assembly (mm).
 * @returns Notch depth (mm). 0 for "none".*/
export function notchDepthFor(cutTarget: CutTarget, assemblyThickness: number): number {
  if (cutTarget === "none") return 0;
  return assemblyThickness;
}
